/**
 * DataTables の ColVis / Export / filter / length を chrome 帯のスロットへ寄せ、
 * 表上の二重ツール行をなくす。⋯ メニューから dock 済みボタンを開く。
 */

/** 件数セレクトを chrome に載せる最小幅（未満はフッタ付近へ） */
export const DATATABLES_LENGTH_CHROME_MIN_PX = 1600;

type TranslateFn = (key: string, options?: { defaultValue?: string }) => string;

/** length の aria（短）/ title（全文）を i18n から揃える */
export function dataTablesLengthA11yLabels(t: TranslateFn): {
  lengthAriaLabel: string;
  lengthTitle: string;
} {
  return {
    lengthAriaLabel: t('datatables.length_aria', { defaultValue: '表示件数' }),
    lengthTitle: t('datatables.length_title', {
      defaultValue: '1ページあたりの表示件数を変更',
    }),
  };
}

function clickFirst(selector: string): boolean {
  if (typeof window === 'undefined') return false;
  const el = document.querySelector(selector);
  if (!(el instanceof HTMLElement)) return false;
  el.click();
  return true;
}

function moveIntoSlot(el: Element | null, slot: Element | null): boolean {
  if (!el || !slot) return false;
  if (el.parentElement !== slot) {
    slot.appendChild(el);
  }
  slot.removeAttribute('aria-hidden');
  return true;
}

/**
 * DataTables の ColVis / Export を stats 帯のスロットへ寄せる。
 */
export function dockSampleListExportButtons(): void {
  if (typeof window === 'undefined') return;
  const slot = document.querySelector('#sample-manager-page .sample-list-export-slot');
  const wrapper = document.querySelector('#sample-manager-table-wrapper .dataTables_wrapper');
  if (!slot || !wrapper) return;

  const buttons =
    (slot.querySelector('.dt-buttons') as HTMLElement | null) ||
    (wrapper.querySelector(':scope > .dt-buttons') as HTMLElement | null) ||
    (wrapper.querySelector('.dt-buttons') as HTMLElement | null);
  if (!buttons) return;

  if (buttons.parentElement !== slot) {
    slot.appendChild(buttons);
  }
  slot.removeAttribute('aria-hidden');
}

type FilterLengthDockOpts = {
  wrapperSelector: string;
  filterSlotSelector?: string;
  lengthSlotSelector?: string;
  lengthChromeMinPx?: number;
  lengthAriaLabel?: string;
  /** hover / title（省略時は lengthAriaLabel） */
  lengthTitle?: string;
};

/**
 * filter を chrome 検索スロットへ。length は幅が足りるときだけ chrome、それ以外は info 前へ。
 */
export function dockDataTablesFilterAndLength(opts: FilterLengthDockOpts): void {
  if (typeof window === 'undefined') return;
  const wrapper = document.querySelector(opts.wrapperSelector);
  if (!wrapper) return;

  const filterSlot = opts.filterSlotSelector
    ? document.querySelector(opts.filterSlotSelector)
    : null;
  const lengthSlot = opts.lengthSlotSelector
    ? document.querySelector(opts.lengthSlotSelector)
    : null;

  const filter =
    (filterSlot?.querySelector('.dataTables_filter') as HTMLElement | null) ||
    (wrapper.querySelector('.dataTables_filter') as HTMLElement | null);

  const length =
    (lengthSlot?.querySelector('.dataTables_length') as HTMLElement | null) ||
    (wrapper.querySelector('.dataTables_length') as HTMLElement | null);

  if (filter && filterSlot) {
    moveIntoSlot(filter, filterSlot);
  }

  if (length && lengthSlot) {
    const minPx = opts.lengthChromeMinPx ?? DATATABLES_LENGTH_CHROME_MIN_PX;
    if (window.innerWidth >= minPx) {
      moveIntoSlot(length, lengthSlot);
    } else {
      const info = wrapper.querySelector('.dataTables_info');
      if (info?.parentElement) {
        if (length.nextSibling !== info) {
          info.parentElement.insertBefore(length, info);
        }
      } else if (length.parentElement !== wrapper) {
        wrapper.appendChild(length);
      }
      lengthSlot.setAttribute('aria-hidden', 'true');
    }
  }

  syncDataTablesLengthA11y(opts.wrapperSelector, {
    lengthAriaLabel: opts.lengthAriaLabel,
    lengthTitle: opts.lengthTitle,
    lengthSlotSelector: opts.lengthSlotSelector,
  });
}

type LengthA11yDockOpts = {
  lengthAriaLabel?: string;
  lengthTitle?: string;
};

/** 保管室一覧の ColVis / Export / filter / length を chrome へ寄せる */
export function dockStorageRoomsChrome(opts?: LengthA11yDockOpts): void {
  dockDtButtonsToSlot(
    '#storage-rooms-page .sample-list-export-slot, #storage-rooms-page .storage-rooms-export-slot',
    '#storage-rooms-table-wrapper .dataTables_wrapper'
  );
  dockDataTablesFilterAndLength({
    wrapperSelector: '#storage-rooms-table-wrapper .dataTables_wrapper',
    filterSlotSelector: '#storage-rooms-page .dataTables-filter-slot',
    lengthSlotSelector: '#storage-rooms-page .dataTables-length-slot',
    lengthAriaLabel: opts?.lengthAriaLabel,
    lengthTitle: opts?.lengthTitle,
  });
}

/** レポート一覧の Export をスロットへ寄せる */
export function dockReportsExportButtons(opts?: LengthA11yDockOpts): void {
  dockDtButtonsToSlot(
    '#reports-page .sample-list-export-slot, #reports-page .reports-export-slot',
    '#reports-table-wrapper .dataTables_wrapper, #reports-page .dataTables_wrapper'
  );
  dockDataTablesFilterAndLength({
    wrapperSelector:
      '#reports-table-wrapper .dataTables_wrapper, #reports-page .dataTables_wrapper',
    lengthSlotSelector: '#reports-page .dataTables-length-slot',
    lengthAriaLabel: opts?.lengthAriaLabel,
    lengthTitle: opts?.lengthTitle,
  });
}

function dockDtButtonsToSlot(slotSelector: string, wrapperSelector: string): void {
  if (typeof window === 'undefined') return;
  const slot = document.querySelector(slotSelector);
  const wrapper = document.querySelector(wrapperSelector);
  if (!slot || !wrapper) return;

  const buttons =
    (slot.querySelector('.dt-buttons') as HTMLElement | null) ||
    (wrapper.querySelector(':scope > .dt-buttons') as HTMLElement | null) ||
    (wrapper.querySelector('.dt-buttons') as HTMLElement | null);
  if (!buttons) return;

  if (buttons.parentElement !== slot) {
    slot.appendChild(buttons);
  }
  slot.removeAttribute('aria-hidden');
}

/**
 * display:none の件数目／info が a11y に「102550100 件表示」と漏れるのを防ぐ。
 * HTML hidden は使わない（再表示時に computed display が常に none になる）。
 * select には明示 aria-label を付け、label 内の「件表示」テキストは装飾扱いにする。
 * chrome スロットへ dock 済みの length も lengthSlotSelector で対象に含める。
 */
export function syncDataTablesLengthA11y(
  wrapperSelector: string,
  options?: {
    lengthAriaLabel?: string;
    /** hover。省略時は lengthAriaLabel */
    lengthTitle?: string;
    lengthSlotSelector?: string;
  }
): void {
  if (typeof window === 'undefined') return;
  const lengthAriaLabel = options?.lengthAriaLabel?.trim();
  const lengthTitle = options?.lengthTitle?.trim() || lengthAriaLabel;
  const seen = new Set<HTMLElement>();
  const collect = (root: ParentNode | null) => {
    if (!root) return;
    root
      .querySelectorAll<HTMLElement>('.dataTables_length, .dataTables_info')
      .forEach((el) => seen.add(el));
  };
  document.querySelectorAll(wrapperSelector).forEach((wrapper) => collect(wrapper));
  if (options?.lengthSlotSelector) {
    collect(document.querySelector(options.lengthSlotSelector));
  }

  seen.forEach((el) => {
    const style = window.getComputedStyle(el);
    const hidden = style.display === 'none' || style.visibility === 'hidden';
    const select = el.querySelector('select');

    if (select && lengthAriaLabel) {
      select.setAttribute('aria-label', lengthAriaLabel);
      if (lengthTitle) select.setAttribute('title', lengthTitle);
    }
    if (el.classList.contains('dataTables_length')) {
      decorateDataTablesLengthLabelText(el);
      // 親 .dataTables_length が option 連結名（102550100…）になるのを防ぐ
      if (lengthAriaLabel) {
        el.setAttribute('role', 'group');
        el.setAttribute('aria-label', lengthAriaLabel);
        if (lengthTitle) el.setAttribute('title', lengthTitle);
      }
    }

    if (hidden) {
      el.setAttribute('aria-hidden', 'true');
      el.inert = true;
      if (select) {
        select.setAttribute('aria-hidden', 'true');
        select.tabIndex = -1;
      }
    } else {
      el.removeAttribute('aria-hidden');
      el.inert = false;
      if (select) {
        select.removeAttribute('aria-hidden');
        select.removeAttribute('tabindex');
      }
    }
  });
}

/** label 内 select のオプション連結名（102550100…）を避ける */
function decorateDataTablesLengthLabelText(lengthEl: HTMLElement): void {
  const label = lengthEl.querySelector('label');
  const select = lengthEl.querySelector('select');
  if (!label || !select) return;

  // select を label の外へ（options が accessible name に混ざるのを防ぐ）
  if (label.contains(select)) {
    lengthEl.insertBefore(select, label);
  }

  label.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (!text.trim()) {
        node.textContent = '';
        return;
      }
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = text;
      label.replaceChild(span, node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      (node as HTMLElement).setAttribute('aria-hidden', 'true');
    }
  });
  label.setAttribute('aria-hidden', 'true');
}

/** 保管室 DT の filter 入力に aria-label（search ラベルを空にしたとき用） */
export function decorateDataTablesFilterA11y(
  wrapperSelector: string,
  ariaLabel: string
): void {
  if (typeof window === 'undefined') return;
  const input =
    (document.querySelector(
      `${wrapperSelector} .dataTables_filter input`
    ) as HTMLInputElement | null) ||
    (document.querySelector('.dataTables-filter-slot .dataTables_filter input') as HTMLInputElement | null);
  if (!input) return;
  input.setAttribute('aria-label', ariaLabel);
  if (!input.getAttribute('placeholder')) {
    input.setAttribute('placeholder', ariaLabel);
  }
}

/** ⋯ → 詳細フィルター（ページ内トグルをクリック） */
export function openDetailedFilterToggle(pageSelector: string): boolean {
  return clickFirst(`${pageSelector} .keyword-detailed-filter-toggle`);
}

/** ⋯ → コンパクトヘルプ */
export function openCompactHelp(pageSelector: string): boolean {
  return clickFirst(`${pageSelector} .help-trigger-compact`);
}

/**
 * compactChrome では export スロットが d-none のため、ColVis/Export の
 * プログラムクリックだけでは DT コレクションが座標 0 付近か非表示になる。
 * ⋯ トグル直下へ一時配置してから開く。
 */
let dockedDtHostRelease: (() => void) | null = null;

export function releaseDockedDtButtonsHost(): void {
  dockedDtHostRelease?.();
  dockedDtHostRelease = null;
}

function armDockedDtButtonsHost(pageSelector: string): void {
  releaseDockedDtButtonsHost();
  if (typeof window === 'undefined') return;

  const root = document.querySelector(pageSelector);
  const slot = root?.querySelector('.sample-list-export-slot');
  if (!(slot instanceof HTMLElement)) return;

  const toggle =
    (root?.querySelector('.sample-list-tools-toggle') as HTMLElement | null) ||
    (root?.querySelector('[id$="-tools-toggle"]') as HTMLElement | null);

  const hadDNone = slot.classList.contains('d-none');
  const prev = {
    position: slot.style.position,
    top: slot.style.top,
    right: slot.style.right,
    left: slot.style.left,
    zIndex: slot.style.zIndex,
  };

  if (hadDNone) slot.classList.remove('d-none');
  slot.classList.add('is-dt-dock-host');
  slot.removeAttribute('aria-hidden');

  if (toggle) {
    const r = toggle.getBoundingClientRect();
    slot.style.position = 'fixed';
    slot.style.top = `${Math.round(r.bottom + 2)}px`;
    slot.style.right = `${Math.round(window.innerWidth - r.right)}px`;
    slot.style.left = 'auto';
    slot.style.zIndex = '1080';
  }

  const onDocClick = (ev: Event) => {
    const target = ev.target;
    if (!(target instanceof Node)) return;
    if (slot.contains(target)) return;
    if (target instanceof Element && target.closest('.dt-button-collection')) return;
    window.setTimeout(() => {
      if (!document.querySelector('.dt-button-collection')) {
        releaseDockedDtButtonsHost();
      }
    }, 0);
  };

  dockedDtHostRelease = () => {
    document.removeEventListener('click', onDocClick, true);
    slot.classList.remove('is-dt-dock-host');
    if (hadDNone) slot.classList.add('d-none');
    slot.style.position = prev.position;
    slot.style.top = prev.top;
    slot.style.right = prev.right;
    slot.style.left = prev.left;
    slot.style.zIndex = prev.zIndex;
  };

  window.setTimeout(() => {
    document.addEventListener('click', onDocClick, true);
  }, 0);
}

/** ⋯ → dock 済み ColVis */
export function openDockedColVisMenu(pageSelector: string): boolean {
  armDockedDtButtonsHost(pageSelector);
  const ok =
    clickFirst(`${pageSelector} .sample-list-export-slot .buttons-colvis`) ||
    clickFirst(`${pageSelector} .dt-buttons .buttons-colvis`);
  if (!ok) releaseDockedDtButtonsHost();
  return ok;
}

/** ⋯ → dock 済み Export コレクション */
export function openDockedExportMenu(pageSelector: string): boolean {
  armDockedDtButtonsHost(pageSelector);
  const ok =
    clickFirst(`${pageSelector} .sample-list-export-slot .buttons-export-menu`) ||
    clickFirst(`${pageSelector} .dt-buttons .buttons-export-menu`);
  if (!ok) releaseDockedDtButtonsHost();
  return ok;
}
